'use client';

import {
  Viewer,
  Worker,
  SpecialZoomLevel,
  ScrollMode,
  type DocumentLoadEvent,
  type PageChangeEvent,
} from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import * as pdfjs from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker.entry';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/full-screen/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { fullScreenPlugin } from '@react-pdf-viewer/full-screen';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { useEffect, useState } from 'react';
import { trackEvent } from 'services/Gtm/functions';
import getEarningReportPageTestId from 'constants/testsIds/earningsReportPage';
import * as S from './PDFViewerStyles';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface PDFViewerInterface {
  urlPDF: string;
}

const PDFViewer = ({ urlPDF }: PDFViewerInterface) => {
  const toolbarPluginInstance = toolbarPlugin();
  const zoomPluginInstance = zoomPlugin();
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const [pageCount, setPageCount] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);

  const { ZoomInButton, ZoomOutButton, ZoomPopover } = zoomPluginInstance;
  const { GoToPreviousPage, GoToNextPage } = pageNavigationPluginInstance;
  const { Toolbar } = toolbarPluginInstance;

  const fullScreenPluginInstance = fullScreenPlugin({
    renderExitFullScreenButton: () => (
      <S.FullScreenButtonsContainer>
        <S.ZoomButtonContainer data-cy={getEarningReportPageTestId('PDF_VIEWER_BUTTONS_CONTAINER')}>
          <span data-cy={getEarningReportPageTestId('ZOOM_OUT_BUTTON')}>
            <ZoomOutButton />
          </span>
          <span data-cy={getEarningReportPageTestId('ZOOM_POPOVER')}>
            <ZoomPopover />
          </span>
          <span data-cy={getEarningReportPageTestId('ZOOM_IN_BUTTON')}>
            <ZoomInButton />
          </span>
          <span data-cy={getEarningReportPageTestId('EXIT_FULL_SCREEN_BUTTON')}>
            <fullScreenPluginInstance.EnterFullScreen />
          </span>
          <span data-cy={getEarningReportPageTestId('PREVIOUS_PAGE_BUTTON')}>
            <GoToPreviousPage />
          </span>
          <span data-cy={getEarningReportPageTestId('NEXT_PAGE_BUTTON')}>
            <GoToNextPage />
          </span>
        </S.ZoomButtonContainer>
      </S.FullScreenButtonsContainer>
    ),
  });

  const [isHydrated, setIsHydrated] = useState(false);

  // this is needed for rendering on fullscreen on mobile if this is not used the PDF viewer will be stuck on "loading"
  const handleFullScreen = (onClick: () => void) => {
    onClick();
    window.dispatchEvent(new Event('resize'));
  };

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const onDocumentLoad = (e: DocumentLoadEvent) => {
    setPageCount(e?.doc?.numPages);
  };

  const onPageChange = (e: PageChangeEvent) => {
    setCurrentPage(e.currentPage + 1);
  };

  useEffect(() => {
    if (!pageCount) {
      return;
    }

    trackEvent({
      customData: {
        content_placement: `earnings_report_slide_show_p${currentPage}`,
        earnings_report_slide_show_total_pages: pageCount,
      },
      eventCategory: 'slideshow',
    });
  }, [pageCount, currentPage]);

  return (
    <>
      <Toolbar>
        {() => (
          <>
            {isHydrated && (
              <S.ZoomButtonContainer data-cy={getEarningReportPageTestId('PDF_VIEWER_BUTTONS_CONTAINER')}>
                <span data-cy={getEarningReportPageTestId('ZOOM_OUT_BUTTON')}>
                  <ZoomOutButton />
                </span>
                <span data-cy={getEarningReportPageTestId('ZOOM_POPOVER')}>
                  <ZoomPopover />
                </span>
                <span data-cy={getEarningReportPageTestId('ZOOM_IN_BUTTON')}>
                  <ZoomInButton />
                </span>
                <fullScreenPluginInstance.EnterFullScreen>
                  {(propsFullScreen) => (
                    <button
                      data-cy={getEarningReportPageTestId('FULL_SCREEN_BUTTON')}
                      aria-label='Enter Full Screen'
                      type='button'
                      onClick={() => handleFullScreen(() => propsFullScreen.onClick())}
                    >
                      <fullScreenPluginInstance.EnterFullScreenButton />
                    </button>
                  )}
                </fullScreenPluginInstance.EnterFullScreen>
                <span data-cy={getEarningReportPageTestId('PREVIOUS_PAGE_BUTTON')}>
                  <GoToPreviousPage />
                </span>
                <span data-cy={getEarningReportPageTestId('NEXT_PAGE_BUTTON')}>
                  <GoToNextPage />
                </span>
              </S.ZoomButtonContainer>
            )}{' '}
          </>
        )}
      </Toolbar>

      <S.PDFContainer data-cy={getEarningReportPageTestId('PDF_VIEWER_CONTAINER')}>
        <Worker
          workerUrl={pdfjs.GlobalWorkerOptions.workerSrc}
          data-cy='sad'
        >
          <Viewer
            fileUrl={urlPDF}
            defaultScale={SpecialZoomLevel.PageFit}
            plugins={[zoomPluginInstance, fullScreenPluginInstance, pageNavigationPluginInstance]}
            scrollMode={ScrollMode.Page}
            onDocumentLoad={onDocumentLoad}
            onPageChange={onPageChange}
          />
        </Worker>
      </S.PDFContainer>
    </>
  );
};

export default PDFViewer;
